import {ActionMenu, ActionList, IconButton} from '@primer/react'
import {
  KebabHorizontalIcon,
  AlertIcon,
  CommentDiscussionIcon,
  GearIcon,
  ChevronRightIcon,
  VersionsIcon,
} from '@primer/octicons-react'
import {useState, useRef, useCallback, useMemo} from 'react'
import {DiffViewSettingsMenuContent} from '@github-ui/diff-view-settings'
import {ensurePreviousActiveDialogIsClosed} from '@github-ui/diff-lines/ensure-previous-active-dialog-is-closed'
import {usePullRequestAnalytics} from '../../hooks/use-pull-request-analytics'
import {useCommitSelectorLabel} from '../../hooks/use-commit-selector-label'
import {useAnnotations, useUnresolvedThreadCount, useUnresolvedConversationCount} from '../../stores/selectors'
import {useDockedPanelActions} from '../../contexts/DockedPanelContext'
import {isModernPullRequestExperienceEnabled} from '@github-ui/pull-request-feature-flags'

import styles from './ToolbarMoreMenu.module.css'

export interface ToolbarMoreMenuProps {
  hideWhitespace: boolean
  onUpdateWhitespace: (value: boolean) => void
  additionalPreferences?: React.ReactNode
  showCommitSelector: () => void
  commits: Array<{oid: string}>
  isFileTreeExpanded?: boolean
}

export function ToolbarMoreMenu({
  hideWhitespace,
  onUpdateWhitespace,
  additionalPreferences,
  showCommitSelector,
  commits,
  isFileTreeExpanded = true,
}: ToolbarMoreMenuProps) {
  const commitOids = useMemo(() => commits.map(commit => commit.oid), [commits])
  const commitSelectorLabel = useCommitSelectorLabel(commitOids)

  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isSettingsOpen, setIsSettingsOpen] = useState(false)
  const menuButtonRef = useRef<HTMLButtonElement>(null)

  const showUpdatedHeader = isModernPullRequestExperienceEnabled()
  const annotations = useAnnotations()
  const unresolvedConversationCount = useUnresolvedConversationCount()
  const legacyDiffCommentCount = useUnresolvedThreadCount()
  const diffCommentCount = showUpdatedHeader ? (unresolvedConversationCount ?? 0) : legacyDiffCommentCount

  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()
  const dockedPanelActions = useDockedPanelActions()

  const totalAnnotationsCount = annotations.length

  const handleOpenComments = useCallback(() => {
    sendPullRequestAnalyticsEvent('comments_side_panel.open', 'COMMENTS_SIDE_PANEL_BUTTON')
    ensurePreviousActiveDialogIsClosed()
    dockedPanelActions.openPanel('comments', {returnFocusTo: menuButtonRef.current})
    setIsMenuOpen(false)
  }, [sendPullRequestAnalyticsEvent, dockedPanelActions])

  const handleOpenAlerts = useCallback(() => {
    sendPullRequestAnalyticsEvent('alerts_side_panel.open', 'ALERTS_SIDE_PANEL_BUTTON')
    ensurePreviousActiveDialogIsClosed()
    dockedPanelActions.openPanel('alerts', {returnFocusTo: menuButtonRef.current})
    setIsMenuOpen(false)
  }, [sendPullRequestAnalyticsEvent, dockedPanelActions])

  return (
    <>
      <ActionMenu open={isMenuOpen} onOpenChange={setIsMenuOpen}>
        <ActionMenu.Anchor>
          <IconButton
            ref={menuButtonRef}
            icon={KebabHorizontalIcon}
            variant="default"
            size="small"
            aria-label="More options"
          />
        </ActionMenu.Anchor>
        <ActionMenu.Overlay align="end">
          <ActionList>
            <ActionList.Group>
              <ActionList.Item onSelect={handleOpenComments}>
                <ActionList.LeadingVisual>
                  <CommentDiscussionIcon />
                </ActionList.LeadingVisual>
                Comments
                {diffCommentCount > 0 && <ActionList.TrailingVisual>{diffCommentCount}</ActionList.TrailingVisual>}
              </ActionList.Item>
              {totalAnnotationsCount > 0 && (
                <ActionList.Item onSelect={handleOpenAlerts}>
                  <ActionList.LeadingVisual>
                    <AlertIcon />
                  </ActionList.LeadingVisual>
                  Alerts
                  <ActionList.TrailingVisual>{totalAnnotationsCount}</ActionList.TrailingVisual>
                </ActionList.Item>
              )}
              <ActionList.Item
                onSelect={() => showCommitSelector()}
                className={styles[`show-commit-selector-on-${isFileTreeExpanded ? 'small' : 'medium'}`]}
              >
                <ActionList.LeadingVisual>
                  <VersionsIcon />
                </ActionList.LeadingVisual>
                {commitSelectorLabel}
              </ActionList.Item>
            </ActionList.Group>
            <ActionList.Divider />
            <ActionList.Group>
              <ActionMenu open={isSettingsOpen} onOpenChange={setIsSettingsOpen}>
                <ActionMenu.Anchor>
                  <ActionList.Item>
                    <ActionList.LeadingVisual>
                      <GearIcon />
                    </ActionList.LeadingVisual>
                    Settings
                    <ActionList.TrailingVisual>
                      <ChevronRightIcon />
                    </ActionList.TrailingVisual>
                  </ActionList.Item>
                </ActionMenu.Anchor>
                <ActionMenu.Overlay side="outside-bottom" align="end">
                  <ActionList>
                    <DiffViewSettingsMenuContent
                      reloadOnWhitespaceChange
                      onUpdateWhitespace={onUpdateWhitespace}
                      defaultHideWhitespace={hideWhitespace}
                      additionalPreferences={additionalPreferences}
                    />
                  </ActionList>
                </ActionMenu.Overlay>
              </ActionMenu>
            </ActionList.Group>
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
    </>
  )
}
